import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Eye, EyeOff, Inbox } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Wordmark } from "@/components/site/Navbar";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TYPES = {
  "request-care": "Care Requests",
  referral: "Referrals",
  consultation: "Consultations",
  contact: "Messages",
  application: "Applications",
};

const CHIP = {
  "request-care": "bg-sage-mist text-teal-deep border-sage/50",
  referral: "bg-teal/10 text-teal-deep border-teal/30",
  consultation: "bg-sand text-olive border-olive/20",
  contact: "bg-sage/15 text-olive border-sage/40",
  application: "bg-olive/5 text-olive border-olive/25",
};

function nameOf(sub) {
  const f = sub.fields.find((x) => /name/i.test(x.label));
  return f ? f.value : "—";
}

export default function AdminDashboard() {
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [s, st] = await Promise.all([api.get("/admin/submissions"), api.get("/admin/stats")]);
      setSubs(s.data);
      setStats(st.data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function toggleRead(sub) {
    try {
      await api.patch(`/admin/submissions/${sub.id}/read`, { read: !sub.read });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  }

  async function remove(sub) {
    try {
      await api.delete(`/admin/submissions/${sub.id}`);
      toast.success("Submission deleted.");
      setActive(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  }

  async function logout() {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  }

  const filtered = tab === "all" ? subs : subs.filter((s) => s.type === tab);

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-olive/10 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <Wordmark />
            <Badge variant="outline" className="border-teal/40 text-teal-deep">Admin</Badge>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setLoading(true);
                load();
              }}
              data-testid="admin-refresh-btn"
              aria-label="Refresh"
              className="grid h-10 w-10 place-items-center rounded-full border border-olive/15 text-olive transition-colors hover:bg-sage/10"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-olive/15 px-4 py-2 text-sm font-semibold text-olive transition-colors hover:bg-sage/10"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {Object.entries(TYPES).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-stat-${key}`}
              className={`rounded-2xl border p-5 text-left transition-all duration-300 hover:border-teal/50 ${
                tab === key ? "border-teal-deep bg-white shadow-sm" : "border-olive/10 bg-white"
              }`}
            >
              <p className="text-xs font-bold uppercase tracking-wider text-olive/60">{label}</p>
              <p className="mt-2 font-serif text-3xl text-ink">{stats[key]?.total ?? 0}</p>
              {(stats[key]?.unread ?? 0) > 0 && (
                <p className="mt-1 text-xs font-semibold text-teal-deep">{stats[key].unread} unread</p>
              )}
            </button>
          ))}
        </div>

        <div className="mt-10">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex-wrap bg-sand/70">
              <TabsTrigger value="all" data-testid="admin-tab-all">All ({stats._all?.total ?? 0})</TabsTrigger>
              {Object.entries(TYPES).map(([key, label]) => (
                <TabsTrigger key={key} value={key} data-testid={`admin-tab-${key}`}>
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="mt-6 overflow-hidden rounded-2xl border border-olive/10 bg-white">
            {loading ? (
              <p className="p-10 text-center text-olive/60" data-testid="admin-loading">Loading…</p>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center p-14 text-center" data-testid="admin-empty">
                <Inbox className="h-8 w-8 text-olive/30" />
                <p className="mt-4 text-olive/60">No submissions yet.</p>
              </div>
            ) : (
              <ul data-testid="admin-submissions-list">
                {filtered.map((sub) => (
                  <li
                    key={sub.id}
                    className="flex flex-wrap items-center gap-4 border-b border-olive/10 px-6 py-4 last:border-0"
                  >
                    <span
                      className={`h-2.5 w-2.5 shrink-0 rounded-full ${sub.read ? "bg-olive/20" : "bg-teal"}`}
                      title={sub.read ? "Read" : "Unread"}
                    />
                    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${CHIP[sub.type] || ""}`}>
                      {TYPES[sub.type] || sub.type}
                    </span>
                    <span className={`min-w-0 flex-1 truncate text-sm ${sub.read ? "text-olive/60" : "font-semibold text-ink"}`}>
                      {nameOf(sub)}
                    </span>
                    <span className="text-xs text-olive/50">
                      {new Date(sub.created_at).toLocaleString()}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setActive(sub);
                          if (!sub.read) toggleRead(sub);
                        }}
                        data-testid={`admin-view-${sub.id}`}
                        aria-label="View"
                        className="grid h-9 w-9 place-items-center rounded-full text-teal-deep transition-colors hover:bg-sage/15"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleRead(sub)}
                        data-testid={`admin-toggle-read-${sub.id}`}
                        aria-label={sub.read ? "Mark unread" : "Mark read"}
                        className="grid h-9 w-9 place-items-center rounded-full text-olive/60 transition-colors hover:bg-sage/15"
                      >
                        {sub.read ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => remove(sub)}
                        data-testid={`admin-delete-${sub.id}`}
                        aria-label="Delete"
                        className="grid h-9 w-9 place-items-center rounded-full text-destructive/70 transition-colors hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </main>

      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent className="max-w-lg bg-cream" data-testid="admin-submission-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">
              {active ? TYPES[active.type] || active.type : ""}
            </DialogTitle>
            <DialogDescription>
              {active && `Received ${new Date(active.created_at).toLocaleString()}`}
            </DialogDescription>
          </DialogHeader>
          {active && (
            <dl className="mt-2 space-y-4">
              {active.fields.map((f, i) => (
                <div key={i} className="rounded-xl border border-olive/10 bg-white px-4 py-3">
                  <dt className="text-xs font-bold uppercase tracking-wider text-teal">{f.label}</dt>
                  <dd className="mt-1 whitespace-pre-wrap text-sm text-ink">{f.value || "—"}</dd>
                </div>
              ))}
            </dl>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
